"use client";

import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import RadioGroup from "@/components/RadioGroup/RadioGroup";
import { FaRegAddressCard } from "react-icons/fa";
import { IoIosSearch } from "react-icons/io";
import { useForm, Controller } from "react-hook-form";
import Viewer from "@/components/pdf/Viewer";
import AltchaGate from "@/components/Altcha/AltchaGate";

export default function FormAcuerdoResponsabilidad({ show, api, user = null }) {
  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      identification: "",
      name: "",
      address: "",
      email: "",
      phone: "",
      tipoPersona: "natural",
    },
  });

  const tipoPersona = watch("tipoPersona");
  const [loading, setLoading] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const altchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [step, setStep] = useState(1);

  const resetAltcha = () => {
    setCaptchaToken(null);
    altchaRef.current?.reset?.();
  };

  const searchPersona = async () => {
    const currentIdentification = watch("identification");

    if (!currentIdentification) {
      show({
        type: "warning",
        title: "",
        message: "Por favor, ingresa una Cédula/RUC para buscar.",
      });
      return;
    }

    if (tipoPersona === "natural" && currentIdentification.length !== 10) {
      show({
        type: "warning",
        title: "",
        message: "La cédula debe tener 10 dígitos.",
      });
      return;
    }

    if (tipoPersona === "juridica" && currentIdentification.length !== 13) {
      show({
        type: "warning",
        title: "",
        message: "El RUC debe tener 13 dígitos.",
      });
      return;
    }

    if (!user && !captchaToken) {
      show({
        type: "error",
        title: "Error",
        message: "Por favor, completa la validación para continuar.",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(currentIdentification, captchaToken);

      if (response.data.data.solicitante) {
        setValue("name", response.data.data.solicitante || "");
        setStep(2);
      }
    } catch (error) {
      show({
        type: "error",
        title: "Error",
        message: error.response?.data?.msg || "Error al buscar persona.",
      });

      setValue("name", "");
      setValue("address", "");
      setValue("email", "");
      setValue("phone", "");
    } finally {
      setLoading(false);
      resetAltcha();
    }
  };

  const onSubmit = async (data) => {
    const emailValidation = validarEmail(data.email);
    if (emailValidation !== true) {
      show({ type: "warning", title: "Atención", message: emailValidation });
      return;
    }

    setLoading(true);
    try {
      const response = await api.post({
        solicitanteNombre: data.name,
        solicitanteIdentificacion: data.identification,
        solicitanteCorreo: data.email,
        solicitanteTelefono: data.phone,
      });

      if (response.data.data) {
        show({
          type: "success",
          title: "Éxito",
          message: "Documento generado correctamente.",
        });
        setPdfBase64(response.data.data);
        setPdfLoading(false);
      } else {
        show({
          type: "error",
          title: "Error",
          message: response.message || "Error al generar el documento.",
        });
      }
    } catch (error) {
      console.log(error);
      show({
        type: "error",
        title: "Error",
        message: "Error de red o del servidor.",
      });
    } finally {
      setLoading(false);
    }
  };

  const validarEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email) ? true : "Ingresa un correo electrónico válido";
  };

  return (
    <div>
      {pdfBase64 ? (
        <div className="flex flex-col items-center">
          <Viewer isLoading={pdfLoading} base64={pdfBase64} />
          <button
            type="button"
            onClick={() => {
              setPdfBase64(null);
              setStep(1);
              setValue("identification", "");
              setValue("name", "");
              setValue("address", "");
              setValue("email", "");
              setValue("phone", "");
              setValue("tipoPersona", "natural");
              resetAltcha();
            }}
            className="mt-4 rounded bg-gray-600 px-6 py-2 text-white transition-colors hover:bg-gray-700"
          >
            Nueva Consulta
          </button>
        </div>
      ) : (
        <>
          {step === 1 && (
            <div className="flex flex-col place-items-center gap-4 border px-16 py-8 shadow">
              <div className="flex flex-col gap-4">
                <RadioGroup
                  name="tipoPersona"
                  control={control}
                  label="Tipo de Persona"
                  options={[
                    { label: "Natural", value: "natural" },
                    { label: "Jurídica", value: "juridica" },
                  ]}
                />

                <Controller
                  name="identification"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (tipoPersona === "natural" && value.length !== 10) {
                        return "La cédula debe tener 10 caracteres.";
                      }

                      if (tipoPersona === "juridica" && value.length !== 13) {
                        return "El RUC debe tener 13 caracteres.";
                      }

                      return true;
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Identificación
                      </label>

                      <div className="flex w-125 items-center rounded-lg border border-gray-300 bg-white px-3 py-2">
                        <FaRegAddressCard />
                        <input
                          {...field}
                          id="identification"
                          placeholder={
                            tipoPersona === "natural"
                              ? "Ingrese el número de cédula a consultar"
                              : "Ingrese el RUC a consultar"
                          }
                          type="text"
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(/[^0-9]/g, "");
                            field.onChange(e);
                          }}
                          className="peer ml-2 w-full flex-1 bg-transparent outline-none"
                          maxLength={13}
                        />
                      </div>

                      {fieldState.error && (
                        <span className="mt-1 text-sm text-red-500">
                          {fieldState.error.message}
                        </span>
                      )}
                    </div>
                  )}
                />
              </div>

              <button
                type="button"
                onClick={handleSubmit(searchPersona)}
                disabled={loading || (!user && !captchaToken)}
                className="text-md flex w-fit items-center gap-2 rounded bg-primary px-4 py-2 text-white hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                ) : (
                  <>
                    <IoIosSearch />
                    Buscar
                  </>
                )}
              </button>

              {!user && (
                <AltchaGate
                  ref={altchaRef}
                  className="flex justify-center"
                  onToken={setCaptchaToken}
                />
              )}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {step === 2 && (
              <div>
                <div>
                  <label className="block py-2 text-sm font-medium">
                    Solicitante
                  </label>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <input
                        disabled
                        type="text"
                        {...field}
                        className="w-full rounded border border-gray-300 px-3 py-2"
                        required
                      />
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: "El correo electrónico es requerido",
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Ingresa un correo electrónico válido",
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <div>
                        <label className="block py-2 text-sm font-medium">
                          E-mail
                        </label>
                        <input
                          {...field}
                          type="email"
                          className="w-full rounded border border-gray-300 px-3 py-2"
                        />
                        {fieldState.error && (
                          <span className="mt-1 text-sm text-red-500">
                            {fieldState.error.message}
                          </span>
                        )}
                      </div>
                    )}
                  />

                  <Controller
                    name="phone"
                    control={control}
                    rules={{
                      required: "El teléfono es requerido",
                    }}
                    render={({ field, fieldState }) => (
                      <div>
                        <label className="block py-2 text-sm font-medium">
                          Teléfono
                        </label>
                        <input
                          {...field}
                          type="tel"
                          className="w-full rounded border border-gray-300 px-3 py-2"
                        />
                        {fieldState.error && (
                          <span className="mt-1 text-sm text-red-500">
                            {fieldState.error.message}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>

                <Controller
                  name="address"
                  control={control}
                  rules={{
                    required: "La dirección es requerida",
                  }}
                  render={({ field, fieldState }) => (
                    <div>
                      <label className="block py-2 text-sm font-medium">
                        Dirección
                      </label>
                      <input
                        {...field}
                        type="text"
                        className="w-full rounded border border-gray-300 px-3 py-2"
                      />
                      {fieldState.error && (
                        <span className="mt-1 text-sm text-red-500">
                          {fieldState.error.message}
                        </span>
                      )}
                    </div>
                  )}
                />

                <div className="flex justify-center space-x-4 py-5">
                  <button
                    type="submit"
                    className="rounded bg-green-600 px-6 py-2 text-white transition-colors hover:bg-green-700"
                  >
                    Generar Documento
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setValue("identification", "");
                      setValue("name", "");
                      setValue("address", "");
                      setValue("email", "");
                      setValue("phone", "");
                      setValue("tipoPersona", "natural");
                      resetAltcha();
                    }}
                    className="rounded bg-gray-600 px-6 py-2 text-white transition-colors hover:bg-gray-700"
                  >
                    Nueva Consulta
                  </button>
                </div>

                <div className="mt-3 rounded-lg bg-gray-100 p-4 text-sm leading-relaxed text-gray-700">
                  <p className="mb-2 font-bold text-justify">NOTA:</p>
                  <p className="mb-2 text-justify">
                    Una vez firmado el Acuerdo de Responsabilidad y Uso de Medios Electrónicos, debe acercarse personalmente con el original su cédula de identidad, ciudadanía o pasaporte a las ventanillas de atención al usuario del Gobierno Autónomo Descentralizado Municipal del Cantón Portoviejo.
                  </p>
                  <p className="mb-2 text-justify">
                    Si usted es una persona autorizada a realizar el trámite de Acuerdo de Responsabilidad y Uso de Medios Electrónicos deberá presentar lo siguiente:
                  </p>
                  <ul className="mb-2 ml-4 list-disc list-inside">
                    <li>Acuerdo de Responsabilidad de Uso de Medios Electrónicos firmados por el titular del trámite.</li>
                    <li>Carta de autorización (formato establecido en la web) o Poder especial o general donde se indique la generación del trámite (recuerde solo deberá presentar 1).</li>
                    <li>Copia legible de la cédula de identidad del titular del trámite.</li>
                  </ul>
                  <p className="text-justify">
                    Adicional, se puede presentar como documento de identidad, la cédula de ciudadanía, el pasaporte o la licencia de conducir.
                  </p>
                </div>
              </div>
            )}
          </form>
        </>
      )}
    </div>
  );
}

FormAcuerdoResponsabilidad.propTypes = {
  show: PropTypes.func.isRequired,
  api: PropTypes.object.isRequired,
  user: PropTypes.object,
};